import { useLanguage } from "@web/lib/contexts/language-context";
import { LoaderCircle, ScanLine } from "lucide-react";

type CameraLoadingProps = {
	imageUrl: string | null;
};

export const CameraLoading = ({ imageUrl }: CameraLoadingProps) => {
	const { t } = useLanguage();

	return (
		<output
			aria-live="polite"
			className="fixed inset-0 z-50 overflow-y-auto bg-black text-white"
		>
			<div className="flex min-h-full flex-col items-center justify-center px-6 py-10">
				<div className="flex w-full max-w-md flex-col items-center text-center">
					<h1 className="text-2xl font-semibold leading-tight tracking-tight sm:text-3xl">
						{t.camera.aiValidationTitle}
					</h1>
					<p className="mt-3 max-w-sm text-sm leading-relaxed text-white/75 sm:text-base">
						{t.camera.aiValidationDescription}
					</p>

					<div className="relative mt-8 w-full max-w-64 rounded-2xl border border-white/20 bg-white/10 p-2 shadow-lg">
						<div className="relative aspect-square overflow-hidden rounded-xl bg-black">
							{imageUrl ? (
								<img
									src={imageUrl}
									alt={t.camera.aiValidationPhoto}
									className="h-full w-full object-contain"
								/>
							) : (
								<ScanLine
									aria-hidden="true"
									className="m-auto h-full w-16 text-primary"
								/>
							)}
							<div
								aria-hidden="true"
								className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"
							/>
							{imageUrl && (
								<div
									aria-hidden="true"
									className="camera-scan pointer-events-none absolute inset-x-4 top-1/2 h-16 -translate-y-1/2"
								>
									<div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/20 to-transparent" />
									<div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 bg-primary/70 blur-sm" />
									<div className="absolute inset-x-0 top-1/2 h-px bg-primary" />
								</div>
							)}
						</div>
						<div className="absolute inset-x-0 -bottom-4 mx-auto w-fit rounded-full border border-white/20 bg-black px-4 py-1.5 text-xs font-medium text-white shadow-lg">
							{t.camera.aiValidationPhoto}
						</div>
					</div>

					<div className="mt-11 flex items-center gap-3 text-sm font-medium text-white">
						<LoaderCircle
							aria-hidden="true"
							className="h-5 w-5 text-primary motion-safe:animate-spin"
						/>
						{t.camera.aiValidationStatus}
					</div>
					<p className="mt-2 text-xs text-white/60">
						{t.camera.aiValidationWait}
					</p>
				</div>
			</div>
		</output>
	);
};
